// src/components/RoomsPages.js
import React from 'react';
import Header from './Header';
import Footer from './Footer';


const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
};

const RoomsPages = ({ propertyValuation }) => {
  
  const propertyDetails = propertyValuation?.propertyAttributes?.propertyDetails;

  const mergeFixtures = (roomPlanFixtures = [], generalFixtures = []) => {
    const merged = [
      ...roomPlanFixtures.map(fixture => ({
        ...fixture,
        // Create a display name by combining category and subcategory
        name: `${fixture.category} - ${fixture.subcategory}`,
        value: fixture.totalCost || 0, // Assuming totalCost represents the value
      })),
      ...generalFixtures.map(fixture => ({
        ...fixture,
        // Create a display name by combining category and subcategory
        name: `${fixture.category} - ${fixture.subcategory}`,
        value: (fixture.totalCost || 0), // Calculate total value
      }))
    ];
    return merged;
  };

  return (
    <>
      {propertyValuation.roomValuations.map((room, index) => {
        const roomName = room.roomName;
        const floorAreaRaw = room.floor.floorArea;
        const floorArea = (Math.round(floorAreaRaw * 10) / 10).toFixed(1); // Round to one decimal place
        const mergedFixtures = mergeFixtures(room.roomplanFixtures, room.generalFixtures);
        const totalValuation = mergedFixtures.reduce((acc, fixture) => acc + fixture.value, 0);
        const roomImageURLs = room.roomImageURLs;
        const floorPlanURL = room.floorplanImageURL;
        

        // Calculate the starting page number (Page 5 onwards)
        const pageNumber1 = index * 2 + 5; // First page for the room
        const pageNumber2 = index * 2 + 6; // Second page for the floor plan

        return (
          <React.Fragment key={room.roomID || index}>
            {/* Page 1: Room Details */}
            <div className={`bg-gray-200 w-[794px] h-[1123px] flex flex-col page-break`}>
              {/* Header */}
              <Header pageNumber={pageNumber1} propertyDetails={propertyDetails} />

              {/* Main Content */}
              <div className="flex-grow overflow-auto p-4">
                {/* Room Name with Floor Area */}
                <h2 className="text-2xl font-bold mb-4">
                  {roomName} - {floorArea} m²
                </h2>

                {/* Fixtures Table */}
                <div className="w-full overflow-x-auto mb-4">
                  <table className="min-w-full border-collapse rounded-lg">
                    <thead>
                      <tr className="bg-gray-400">
                        <th className="px-2 py-1 text-left rounded-tl-lg">Fixture Name</th>
                        <th className="px-2 py-1 text-right rounded-tr-lg">Value</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {mergedFixtures.length > 0 ? (
                        mergedFixtures.map((fixture, fixtureIndex) => (
                          <tr key={fixtureIndex} className={'bg-gray-50'}>
                            <td className="px-2 py-1 border-t border-gray-300">{fixture.name || 'N/A'}</td>
                            <td className="px-2 py-1 border-t border-gray-300 text-right">
                              {formatCurrency(fixture.value)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="px-2 py-4 text-center" colSpan="2">
                            No fixtures available.
                          </td>
                        </tr>
                      )}
                      {/* Total Valuation Row */}
                      <tr style={{ backgroundColor: 'rgba(241, 196, 15,0.4)' }}>
                        <td className="px-2 py-1 border-t border-b border-gray-300 font-bold">
                          Total Valuation
                        </td>
                        <td className="px-2 py-1 border-t border-b border-gray-300 text-right font-bold">
                          {formatCurrency(totalValuation)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Room Images */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Room Images</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {roomImageURLs.map((imageURL, imgIndex) => (
                      <div key={imgIndex} className="w-full aspect-w-2 aspect-h-3 overflow-hidden rounded-md">
                        <img
                          src={imageURL.replace('/upload/', '/upload/q_auto:low/')}
                          alt={`${roomName} Image ${imgIndex + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>

                </div>
              </div>

              {/* Footer */}
              <Footer />
            </div>

            {/* Page 2: Floor Plan */}
            <div className={`bg-gray-200 w-[794px] h-[1123px] flex flex-col page-break`}>
              {/* Header */}
              <Header pageNumber={pageNumber2} propertyDetails={propertyDetails} />

              {/* Main Content */}
              <div className="flex-grow overflow-auto p-4">
                {/* Room Name with Floor Area */}
                <h2 className="text-2xl font-bold mb-4">
                  {roomName} - {floorArea} m²
                </h2>

                {/* Floor Plan Image */}
                <div className="w-full">
                  {floorPlanURL ? (
                    <div className="w-full flex justify-center">
                      <img
                        src={floorPlanURL}
                        alt={`${roomName} Floor Plan`}
                        className="max-w-full h-auto object-contain rounded-md"
                      />
                    </div>
                  ) : (
                    <p className="text-sm">No floor plan available.</p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <Footer />
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default RoomsPages;
